export function formatNumberInput(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Parse a plain number or a simple arithmetic expression (`+ - * /`, parentheses,
 * unary ±). Whitespace is allowed. Returns `undefined` when the input is empty
 * or invalid (including division by zero).
 */
export function parseNumericInput(raw: string): number | undefined {
  const source = raw.trim();
  if (!source) {
    return undefined;
  }

  // Fast path for ordinary inspector values.
  if (/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(source)) {
    const value = Number(source);
    return Number.isFinite(value) ? value : undefined;
  }

  const parser = new ExpressionParser(source);
  const value = parser.parseExpression();
  if (value === undefined || !parser.atEnd()) {
    return undefined;
  }
  return Number.isFinite(value) ? value : undefined;
}

class ExpressionParser {
  private readonly source: string;
  private index = 0;

  constructor(source: string) {
    this.source = source;
  }

  atEnd(): boolean {
    this.skipWhitespace();
    return this.index >= this.source.length;
  }

  parseExpression(): number | undefined {
    let left = this.parseTerm();
    if (left === undefined) {
      return undefined;
    }

    while (true) {
      this.skipWhitespace();
      const op = this.peek();
      if (op !== "+" && op !== "-") {
        return left;
      }
      this.index += 1;
      const right = this.parseTerm();
      if (right === undefined) {
        return undefined;
      }
      left = op === "+" ? left + right : left - right;
    }
  }

  private parseTerm(): number | undefined {
    let left = this.parseFactor();
    if (left === undefined) {
      return undefined;
    }

    while (true) {
      this.skipWhitespace();
      const op = this.peek();
      if (op !== "*" && op !== "/") {
        return left;
      }
      this.index += 1;
      const right = this.parseFactor();
      if (right === undefined) {
        return undefined;
      }
      if (op === "/") {
        if (right === 0) {
          return undefined;
        }
        left = left / right;
      } else {
        left = left * right;
      }
    }
  }

  private parseFactor(): number | undefined {
    this.skipWhitespace();
    const sign = this.peek();
    if (sign === "+" || sign === "-") {
      this.index += 1;
      const value = this.parseFactor();
      if (value === undefined) {
        return undefined;
      }
      return sign === "-" ? -value : value;
    }

    if (this.peek() === "(") {
      this.index += 1;
      const value = this.parseExpression();
      this.skipWhitespace();
      if (this.peek() !== ")") {
        return undefined;
      }
      this.index += 1;
      return value;
    }

    return this.parseNumber();
  }

  private parseNumber(): number | undefined {
    this.skipWhitespace();
    const start = this.index;
    while (this.index < this.source.length && isDigit(this.source[this.index]!)) {
      this.index += 1;
    }
    if (this.peek() === ".") {
      this.index += 1;
      while (this.index < this.source.length && isDigit(this.source[this.index]!)) {
        this.index += 1;
      }
    }
    if (this.index === start || (this.source[start] === "." && this.index === start + 1)) {
      return undefined;
    }

    const peek = this.peek();
    if (peek === "e" || peek === "E") {
      const expStart = this.index;
      this.index += 1;
      const expSign = this.peek();
      if (expSign === "+" || expSign === "-") {
        this.index += 1;
      }
      const digitsStart = this.index;
      while (this.index < this.source.length && isDigit(this.source[this.index]!)) {
        this.index += 1;
      }
      if (this.index === digitsStart) {
        this.index = expStart;
      }
    }

    const value = Number(this.source.slice(start, this.index));
    return Number.isFinite(value) ? value : undefined;
  }

  private peek(): string | undefined {
    return this.source[this.index];
  }

  private skipWhitespace(): void {
    while (this.index < this.source.length && isWhitespace(this.source[this.index]!)) {
      this.index += 1;
    }
  }
}

function isDigit(char: string): boolean {
  return char >= "0" && char <= "9";
}

function isWhitespace(char: string): boolean {
  return char === " " || char === "\t" || char === "\n" || char === "\r";
}
